import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { listShiftSummaries, updateShiftSummary, getShiftSummaryById, ShiftSummaryRecord } from '../services/shiftApi';
import { MaintenanceItem, CUSTOMERS, CUSTOMER_COLORS, VALID_MAINTENANCE_CUSTOMERS, ZNUNY_INSTANCES, Customer } from '../types/shift';
import { extractDateFromText } from '../utils/extractDate';

const znunyUrlFor = (customer: string, ticketNumber: string): string | null => {
  const instance = ZNUNY_INSTANCES[customer as Customer];
  if (!instance || !ticketNumber) return null;
  return `https://${instance}/otrs/index.pl?Action=AgentTicketZoom;TicketNumber=${ticketNumber}`;
};

interface CalendarEvent {
  customer: string;
  ticketNumber: string;
  tppTicketNumber?: string;
  description: string;
  status: string;
  scheduledDate: string;
  scheduledTime?: string;
  scheduledEndDate?: string | null;
  shiftDate: string;
  shiftType: string;
  submittedBy: string;
  shiftSummaryId: string;
  maintenanceItemIndex: number;
}

const STORAGE_KEY = 'noc-calendar-filters';

function getStoredFilters(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return Object.fromEntries(CUSTOMERS.map((c) => [c, true]));
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getWeekDates(date: Date): Date[] {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function formatDateKey(d: Date): string {
  return d.toISOString().split('T')[0];
}

/** Calculate age in days from a date string to today */
function getAgeDays(shiftDate: string): number {
  if (!shiftDate) return -1;
  const submitted = new Date(shiftDate + 'T00:00');
  if (isNaN(submitted.getTime())) return -1;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.floor((now.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24));
}

/** Return border color for stale ticket aging */
function getAgingBorderColor(days: number): string | undefined {
  if (days >= 14) return '#ef4444'; // red
  if (days >= 7) return '#f59e0b';  // amber
  return undefined;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ChangeCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkAppliedRef = useRef(false);

  const [viewDate, setViewDate] = useState(() => {
    // Honor ?month=YYYY-MM on mount so deep-links land on the right month.
    const month = new URLSearchParams(window.location.search).get('month');
    if (month && /^\d{4}-\d{2}$/.test(month)) {
      const d = new Date(`${month}-01T00:00`);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
  });
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [filters, setFilters] = useState<Record<string, boolean>>(getStoredFilters);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedEvent, setEditedEvent] = useState<CalendarEvent | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const fetchEvents = useCallback(async () => {
    try {
      const summaries = await listShiftSummaries(100);
      const parsed: CalendarEvent[] = [];

      for (const s of summaries) {
        let items: MaintenanceItem[] = [];
        try {
          let p = JSON.parse(s.maintenanceItemsJson ?? '[]');
          if (typeof p === 'string') p = JSON.parse(p);
          items = Array.isArray(p) ? p : [];
        } catch { continue; }

        for (let idx = 0; idx < items.length; idx++) {
          const m = items[idx];
          // Only show on calendar if there's an actual scheduledDate (the maintenance window)
          // Items without a date go to the "Unscheduled" section below the calendar
          parsed.push({
            customer: m.customer,
            ticketNumber: m.ticketNumber,
            tppTicketNumber: m.tppTicketNumber,
            description: m.description,
            status: m.status,
            scheduledDate: m.scheduledDate || extractDateFromText(m.status, m.description) || '',
            scheduledTime: m.scheduledTime || undefined,
            scheduledEndDate: m.scheduledEndDate,
            shiftDate: s.date,
            shiftType: s.shift,
            submittedBy: (s as ShiftSummaryRecord & { submittedBy?: string }).submittedBy || s.onDutyTech,
            shiftSummaryId: s.id,
            maintenanceItemIndex: idx,
          });
        }
      }

      // Deduplicate by ticketNumber + scheduledDate (same ticket may appear in multiple shifts)
      const seen = new Set<string>();
      const deduped = parsed.filter((e) => {
        const key = `${e.ticketNumber}-${e.scheduledDate}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setEvents(deduped);
    } catch (err) {
      console.error('Failed to load calendar events:', err);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Resolve ?ticket=<n> deep-link once events are loaded.
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const ticket = searchParams.get('ticket');
    if (!ticket || events.length === 0) return;
    deepLinkAppliedRef.current = true;
    const match = events.find((e) => e.ticketNumber === ticket);
    if (match) {
      setSelectedEvent(match);
      // Make sure the pill's customer is visible — if the filter was off, turn it on.
      setFilters((prev) => (prev[match.customer] === false ? { ...prev, [match.customer]: true } : prev));
    }
    // Clean the URL either way — a missed ticket shouldn't leave stale params.
    setSearchParams({}, { replace: true });
  }, [events, searchParams, setSearchParams]);

  const toggleFilter = (customer: string) => {
    setFilters((prev) => {
      const next = { ...prev, [customer]: !prev[customer] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const filteredEvents = useMemo(
    () => events.filter((e) => filters[e.customer] !== false),
    [events, filters],
  );

  const { eventsByDate, unscheduledEvents } = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    const unscheduled: CalendarEvent[] = [];
    for (const e of filteredEvents) {
      if (e.scheduledDate) {
        if (!map[e.scheduledDate]) map[e.scheduledDate] = [];
        map[e.scheduledDate].push(e);
      } else {
        // Filter out empty/ghost rows with no ticket number and no description
        const hasTicket = e.ticketNumber && e.ticketNumber.trim().length > 0;
        const hasDescription = e.description && e.description.trim().length > 0;
        if (hasTicket || hasDescription) {
          unscheduled.push(e);
        }
      }
    }
    return { eventsByDate: map, unscheduledEvents: unscheduled };
  }, [filteredEvents]);

  /** Calculate age in days from shiftDate to today */
  const getAgeDays = (shiftDate: string): number => {
    if (!shiftDate) return -1;
    const submitted = new Date(shiftDate + 'T00:00');
    if (isNaN(submitted.getTime())) return -1;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.floor((now.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24));
  };

  /** Return border color for stale ticket aging */
  const getAgingBorderColor = (days: number): string | undefined => {
    if (days >= 14) return '#ef4444'; // red
    if (days >= 7) return '#f59e0b';  // amber
    return undefined;
  };

  const navigateMonth = (delta: number) => {
    setViewDate((d) => {
      const next = new Date(d);
      next.setMonth(next.getMonth() + delta);
      return next;
    });
  };

  const navigateWeek = (delta: number) => {
    setViewDate((d) => {
      const next = new Date(d);
      next.setDate(next.getDate() + 7 * delta);
      return next;
    });
  };

  const goToday = () => setViewDate(new Date());

  const handleDelete = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      const record = await getShiftSummaryById(selectedEvent.shiftSummaryId);
      if (!record) throw new Error('Shift summary not found');
      let items: MaintenanceItem[] = [];
      try {
        let p = JSON.parse(record.maintenanceItemsJson ?? '[]');
        if (typeof p === 'string') p = JSON.parse(p);
        items = Array.isArray(p) ? p : [];
      } catch { items = []; }
      items.splice(selectedEvent.maintenanceItemIndex, 1);
      await updateShiftSummary({ id: selectedEvent.shiftSummaryId, maintenanceItemsJson: JSON.stringify(items) });
      setConfirmDeleteOpen(false);
      setSelectedEvent(null);
      setSnackbar({ open: true, message: 'Maintenance item deleted', severity: 'success' });
      fetchEvents();
    } catch (err) {
      console.error('Failed to delete maintenance item:', err);
      setSnackbar({ open: true, message: 'Failed to delete maintenance item', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editedEvent || !selectedEvent) return;
    setSaving(true);
    try {
      const record = await getShiftSummaryById(selectedEvent.shiftSummaryId);
      if (!record) throw new Error('Shift summary not found');
      let items: MaintenanceItem[] = [];
      try {
        let p = JSON.parse(record.maintenanceItemsJson ?? '[]');
        if (typeof p === 'string') p = JSON.parse(p);
        items = Array.isArray(p) ? p : [];
      } catch { items = []; }
      items[selectedEvent.maintenanceItemIndex] = {
        ...items[selectedEvent.maintenanceItemIndex],
        customer: editedEvent.customer,
        ticketNumber: editedEvent.ticketNumber,
        description: editedEvent.description,
        status: editedEvent.status,
        scheduledDate: editedEvent.scheduledDate || null,
        scheduledTime: editedEvent.scheduledTime ?? null,
        scheduledEndDate: editedEvent.scheduledEndDate || null,
        tppTicketNumber: editedEvent.tppTicketNumber,
      };
      await updateShiftSummary({ id: selectedEvent.shiftSummaryId, maintenanceItemsJson: JSON.stringify(items) });
      setEditMode(false);
      setSelectedEvent(null);
      setEditedEvent(null);
      setSnackbar({ open: true, message: 'Maintenance item updated', severity: 'success' });
      fetchEvents();
    } catch (err) {
      console.error('Failed to update maintenance item:', err);
      setSnackbar({ open: true, message: 'Failed to update maintenance item', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const today = formatDateKey(new Date());

  // Build month grid
  const monthGrid = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = new Date(year, month, 1).getDay();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const weekDates = useMemo(() => getWeekDates(viewDate), [viewDate]);

  const renderDayCell = (date: Date | null, isWeekView = false) => {
    if (!date) {
      return (
        <Box
          key="empty"
          sx={{ minHeight: isWeekView ? 200 : 100, border: '1px solid rgba(255,255,255,0.04)' }}
        />
      );
    }
    const key = formatDateKey(date);
    const dayEvents = eventsByDate[key] || [];
    const isToday = key === today;

    return (
      <Box
        key={key}
        sx={{
          minHeight: isWeekView ? 200 : 100,
          border: '1px solid rgba(255,255,255,0.06)',
          bgcolor: isToday ? 'rgba(0,188,212,0.06)' : 'transparent',
          p: 0.5,
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: isToday ? 700 : 400,
            color: isToday ? '#00bcd4' : '#94a3b8',
            mb: 0.5,
          }}
        >
          {isWeekView
            ? `${DAY_NAMES[date.getDay()]} ${date.getDate()}`
            : date.getDate()}
        </Typography>
        {dayEvents.slice(0, isWeekView ? 10 : 3).map((e, i) => (
          <Chip
            key={i}
            label={`${e.customer}: #${e.ticketNumber}`}
            aria-label={`${e.customer} ticket ${e.ticketNumber}: ${e.description || 'maintenance'}. Click for details.`}
            size="small"
            onClick={() => setSelectedEvent(e)}
            sx={{
              bgcolor: CUSTOMER_COLORS[e.customer] || '#64748b',
              color: 'white',
              fontSize: '0.6rem',
              height: 20,
              mb: 0.25,
              maxWidth: '100%',
              cursor: 'pointer',
              '& .MuiChip-label': { px: 0.5 },
              '&:focus-visible': { outline: '2px solid #ffffff', outlineOffset: 2 },
            }}
          />
        ))}
        {dayEvents.length > (isWeekView ? 10 : 3) && (
          <Typography sx={{ fontSize: '0.6rem', color: '#64748b' }}>
            +{dayEvents.length - (isWeekView ? 10 : 3)} more
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d4f5c 100%)',
          color: 'white',
          border: '1px solid',
          borderColor: 'primary.dark',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Change Management Calendar
            </Typography>
            <Typography variant="body2" color="#94a3b8">
              Scheduled maintenance and change windows from shift summaries
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { color: '#94a3b8', borderColor: 'rgba(255,255,255,0.1)', '&.Mui-selected': { color: '#00bcd4', bgcolor: 'rgba(0,188,212,0.1)' } } }}
            >
              <ToggleButton value="month">
                <Tooltip title="Month view"><CalendarViewMonthIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="week">
                <Tooltip title="Week view"><ViewWeekIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => (viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1))} sx={{ color: 'white' }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={600} sx={{ minWidth: 200, textAlign: 'center' }}>
            {viewMode === 'month'
              ? viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              : `Week of ${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
          </Typography>
          <IconButton onClick={() => (viewMode === 'month' ? navigateMonth(1) : navigateWeek(1))} sx={{ color: 'white' }}>
            <ChevronRightIcon />
          </IconButton>
          <IconButton onClick={goToday} sx={{ color: '#00bcd4' }}>
            <Tooltip title="Go to today"><TodayIcon /></Tooltip>
          </IconButton>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Customer Filters Sidebar */}
        <Grid item xs={12} sm={3} md={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Customers
            </Typography>
            {CUSTOMERS.map((c) => {
              const isOn = filters[c] !== false;
              return (
                <FormControlLabel
                  key={c}
                  control={
                    <Checkbox
                      checked={isOn}
                      onChange={() => toggleFilter(c)}
                      size="small"
                      inputProps={{ 'aria-label': `${isOn ? 'Hide' : 'Show'} ${c} maintenance` }}
                      sx={{
                        color: CUSTOMER_COLORS[c],
                        '&.Mui-checked': { color: CUSTOMER_COLORS[c] },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: isOn ? 600 : 400,
                        color: isOn ? CUSTOMER_COLORS[c] : '#64748b',
                        transition: 'color 0.15s, font-weight 0.15s',
                      }}
                    >
                      {c}
                    </Typography>
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 0,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.08)' },
                    '&:focus-within': { outline: `2px solid ${CUSTOMER_COLORS[c]}`, outlineOffset: 1 },
                  }}
                />
              );
            })}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} shown
            </Typography>
          </Paper>
        </Grid>

        {/* Calendar Grid */}
        <Grid item xs={12} sm={9} md={10}>
          <Paper sx={{ p: 1, overflow: 'hidden' }}>
            {viewMode === 'month' ? (
              <>
                {/* Day headers */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                  {DAY_NAMES.map((d) => (
                    <Typography
                      key={d}
                      sx={{
                        textAlign: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#64748b',
                        py: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                      }}
                    >
                      {d}
                    </Typography>
                  ))}
                </Box>
                {/* Month cells */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                  {monthGrid.map((date, i) => (
                    <React.Fragment key={i}>
                      {renderDayCell(date)}
                    </React.Fragment>
                  ))}
                </Box>
              </>
            ) : (
              <>
                {/* Week view */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                  {weekDates.map((date) => (
                    <React.Fragment key={formatDateKey(date)}>
                      {renderDayCell(date, true)}
                    </React.Fragment>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Unscheduled Maintenance Items */}
      {unscheduledEvents.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom color="#f59e0b">
            Unscheduled Maintenance ({unscheduledEvents.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These items need a Scheduled Date to appear on the calendar. Click to view details.
          </Typography>
          {unscheduledEvents.map((e, i) => {
            const ageDays = getAgeDays(e.shiftDate);
            const agingColor = getAgingBorderColor(ageDays);
            // Avoid showing ticketNumber when it duplicates description text
            const ticketDisplay = e.ticketNumber && e.ticketNumber.trim() && e.ticketNumber.trim() !== e.description?.trim()
              ? `#${e.ticketNumber}` : null;
            return (
              <Box
                key={i}
                onClick={() => setSelectedEvent(e)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  mb: 1,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  bgcolor: agingColor ? `${agingColor}0A` : 'rgba(255,255,255,0.02)',
                  border: agingColor ? `1px solid ${agingColor}` : '1px solid transparent',
                  '&:hover': { bgcolor: agingColor ? `${agingColor}1A` : 'rgba(0,188,212,0.06)' },
                }}
              >
                <Chip
                  label={e.customer}
                  size="small"
                  sx={{ bgcolor: CUSTOMER_COLORS[e.customer] || '#64748b', color: 'white', fontWeight: 600, minWidth: 70 }}
                />
                {ticketDisplay && (
                  <Typography variant="body2" fontWeight={500}>{ticketDisplay}</Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{e.description}</Typography>
                <Chip label={e.status || 'No status'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                {ageDays >= 7 && (
                  <Chip
                    label={`${ageDays}d old`}
                    size="small"
                    sx={{
                      bgcolor: agingColor,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 20,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Event Detail Dialog */}
      <Dialog
        open={!!selectedEvent}
        onClose={() => { setSelectedEvent(null); setEditMode(false); setEditedEvent(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            color: '#e2e8f0',
            borderRadius: '12px',
            border: '1px solid rgba(0,188,212,0.2)',
          },
        }}
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {editMode && editedEvent ? (
                  <Select
                    size="small"
                    value={editedEvent.customer}
                    onChange={(e) => setEditedEvent({ ...editedEvent, customer: e.target.value })}
                    sx={{ minWidth: 140, color: '#e2e8f0', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                  >
                    {VALID_MAINTENANCE_CUSTOMERS.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Chip
                    label={selectedEvent.customer}
                    size="small"
                    sx={{
                      bgcolor: CUSTOMER_COLORS[selectedEvent.customer] || '#64748b',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}
                {editMode && editedEvent ? (
                  <TextField
                    size="small"
                    label="Ticket #"
                    value={editedEvent.ticketNumber}
                    onChange={(e) => setEditedEvent({ ...editedEvent, ticketNumber: e.target.value })}
                    sx={{ width: 140, input: { color: '#e2e8f0' }, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                  />
                ) : (
                  <Typography fontWeight={600}>
                    #{selectedEvent.ticketNumber}
                  </Typography>
                )}
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="#64748b" textTransform="uppercase">
                    Description
                  </Typography>
                  {editMode && editedEvent ? (
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={editedEvent.description}
                      onChange={(e) => setEditedEvent({ ...editedEvent, description: e.target.value })}
                      sx={{ mt: 0.5, textarea: { color: '#e2e8f0' }, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                    />
                  ) : (
                    <Typography variant="body2">{selectedEvent.description}</Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography variant="caption" color="#64748b" textTransform="uppercase">
                      Scheduled Date
                    </Typography>
                    {editMode && editedEvent ? (
                      <TextField
                        type="date"
                        size="small"
                        value={editedEvent.scheduledDate}
                        onChange={(e) => setEditedEvent({ ...editedEvent, scheduledDate: e.target.value })}
                        sx={{ mt: 0.5, input: { color: '#e2e8f0' }, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                      />
                    ) : (
                      <Typography variant="body2">
                        {selectedEvent.scheduledDate
                          ? new Date(selectedEvent.scheduledDate + 'T00:00').toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'Not scheduled'}
                        {selectedEvent.scheduledTime && (() => {
                          const [h, m] = selectedEvent.scheduledTime!.split(':').map(Number);
                          const suffix = h >= 12 ? 'PM' : 'AM';
                          const h12 = h % 12 || 12;
                          return ` at ${h12}:${String(m).padStart(2, '0')} ${suffix}`;
                        })()}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="#64748b" textTransform="uppercase">
                      End Date
                    </Typography>
                    {editMode && editedEvent ? (
                      <TextField
                        type="date"
                        size="small"
                        value={editedEvent.scheduledEndDate || ''}
                        onChange={(e) => setEditedEvent({ ...editedEvent, scheduledEndDate: e.target.value || null })}
                        sx={{ mt: 0.5, input: { color: '#e2e8f0' }, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                      />
                    ) : selectedEvent.scheduledEndDate ? (
                      <Typography variant="body2">
                        {new Date(selectedEvent.scheduledEndDate + 'T00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="#64748b">—</Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography variant="caption" color="#64748b" textTransform="uppercase">
                      Status
                    </Typography>
                    {editMode && editedEvent ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editedEvent.status}
                        onChange={(e) => setEditedEvent({ ...editedEvent, status: e.target.value })}
                        sx={{ mt: 0.5, input: { color: '#e2e8f0' }, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                      />
                    ) : (
                      <Typography variant="body2">{selectedEvent.status}</Typography>
                    )}
                  </Box>
                  {(selectedEvent.tppTicketNumber || editMode) && (
                    <Box>
                      <Typography variant="caption" color="#64748b" textTransform="uppercase">
                        TPP Ticket#
                      </Typography>
                      {editMode && editedEvent ? (
                        <TextField
                          size="small"
                          value={editedEvent.tppTicketNumber || ''}
                          onChange={(e) => setEditedEvent({ ...editedEvent, tppTicketNumber: e.target.value })}
                          sx={{ mt: 0.5, input: { color: '#e2e8f0' }, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                        />
                      ) : (
                        <Typography variant="body2">{selectedEvent.tppTicketNumber}</Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography variant="caption" color="#64748b" textTransform="uppercase">
                    Reported By
                  </Typography>
                  <Typography variant="body2">
                    {selectedEvent.submittedBy} — {selectedEvent.shiftDate} {selectedEvent.shiftType} shift
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editMode ? (
                  <>
                    <Button
                      size="small"
                      onClick={() => { setEditMode(false); setEditedEvent(null); }}
                      disabled={saving}
                      sx={{ color: '#94a3b8' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveEdit}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={16} /> : undefined}
                      sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#0097a7' } }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setConfirmDeleteOpen(true)}
                      sx={{ '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}
                    >
                      Delete
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => { setEditMode(true); setEditedEvent({ ...selectedEvent }); }}
                      sx={{ color: '#4dd0e1', '&:hover': { bgcolor: 'rgba(0,188,212,0.08)' } }}
                    >
                      Edit
                    </Button>
                  </>
                )}
              </Box>
              {!editMode && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {(() => {
                    const znuny = znunyUrlFor(selectedEvent.customer, selectedEvent.ticketNumber);
                    return znuny ? (
                      <Button
                        variant="outlined"
                        size="small"
                        href={znuny}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        sx={{ color: '#4dd0e1', borderColor: 'rgba(0,188,212,0.4)', '&:hover': { borderColor: '#00bcd4', bgcolor: 'rgba(0,188,212,0.08)' } }}
                      >
                        Open in Znuny
                      </Button>
                    ) : null;
                  })()}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AssignmentIcon />}
                    onClick={() => {
                      const params = new URLSearchParams({ filter: 'maintenance' });
                      if (selectedEvent.ticketNumber) params.set('ticket', selectedEvent.ticketNumber);
                      setSelectedEvent(null);
                      navigate(`/?${params.toString()}`);
                    }}
                    sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#0097a7' } }}
                  >
                    Open in Handoff Form
                  </Button>
                </Box>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !saving && setConfirmDeleteOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            color: '#e2e8f0',
            borderRadius: '12px',
            border: '1px solid rgba(239,68,68,0.3)',
          },
        }}
      >
        <DialogTitle>Delete Maintenance Item</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete this maintenance item? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            size="small"
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={saving}
            sx={{ color: '#94a3b8' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={handleDelete}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ bgcolor: snackbar.severity === 'success' ? '#065f46' : '#7f1d1d', color: '#e2e8f0' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChangeCalendar;
